import React, { useState, useRef, useEffect, useContext } from 'react';
import { Button, Modal, ModalBody } from 'reactstrap';
import { faAddressCard, faIdCardAlt, faUserAlt, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import style from './style';
import { fetchDataAgnostic } from '../../../../../../Services/dataApi';
import { CSVLink } from 'react-csv';
import { DepartmentContext } from '../../../../../../DepartmentWrapper';
import { checkLoginForError, formatBearerToken } from '../../../../../../Common.functions';
import { useAuth0 } from '../../../../../../auth0/reactAuth0Spa';

import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { createPDFForCandidateBadges } from '../../../../../../Services/PDFMakeTemplates/CandidateBadges/CandidateBadges';
import { createPDFForCandidateBadges2 } from '../../../../../../Services/PDFMakeTemplates/CandidateBadges/CandidateBadges_avery_5384';
import { createPDFForCandidateFactCards } from '../../../../../../Services/PDFMakeTemplates/CandidateBadges/CandidateFactCards';
import { createPDFForCandidateFaces } from '../../../../../../Services/PDFMakeTemplates/CandidateBadges/CandidateFaces';
import { createPDFForCandidateFacesWithNotes } from '../../../../../../Services/PDFMakeTemplates/CandidateBadges/CandidateFacesWithNotes';
import { separateDataTypesByAvailability } from '../../../../../../Services/candidates';
import { clone } from 'lodash';
import Loading from 'Body/Statuses/Loading';
import PdfPreviewModal from '../PdfPreviewModal';

// Initialize Day.js plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const PrintDropDownTotal = ({ setShowSettings }) => {
  const candidateCSVDownload = useRef();

  const [candidateCSVData, setCandidateCSVData] = useState([]);
  const [dataTypes, setDataTypes] = useState([]);

  const [isDownloadingCandidates, setIsDownlaodingCandidates] = useState(false);
  const [isProcessingPDF, setIsProcessingPDF] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0 });
  const [showPdfPreviewModal, setShowPdfPreviewModal] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');
  const [pdfPreviewFileName, setPdfPreviewFileName] = useState('');

  const dContext = useContext(DepartmentContext);
  const { getTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (candidateCSVData && candidateCSVData.length > 0) {
      setTimeout(() => {
        candidateCSVDownload.current.link.click();
      }, 1000);

      setTimeout(() => {
        setCandidateCSVData([]);
        if (setShowSettings) {
          setShowSettings(false);
        }
      }, 2000);
    }
  }, [candidateCSVData]);

  useEffect(() => {
    getDepartmentDataTypes((newDataTypes) => {
      const separatedDataTypes = separateDataTypesByAvailability(newDataTypes);
      setDataTypes(separatedDataTypes.enabled);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  const getDepartmentDataTypes = (callback) => {
    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/dataTypes',
          { pk_Department: dContext.department.pk_Department },
          formatBearerToken(token),
        )
          .then((res) => {
            if (callback) {
              callback(res.data);
            }
          })
          .catch((err) => {});
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const getAllCandidatesForDepartment = (callback) => {
    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic(
          'department/season/user/scheduledate/candidates',
          {
            pk_Department: dContext.department.pk_Department,
            pk_Season: dContext.season.pk_Season,
            mode: 'Evaluation',
          },
          formatBearerToken(token),
        )
          .then((res) => {
            if (callback) {
              callback(Object.values(res.data));
            }
          })
          .catch((err) => {
            console.log('err: ', err);

            Swal.fire({
              title: 'Error',
              text: 'Failed to fetch candidates',
              icon: 'error',
            });
          });
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const downloadCandidatesCSV = () => {
    setIsDownlaodingCandidates(true);

    getAllCandidatesForDepartment((validCandidates) => {
      setIsDownlaodingCandidates(false);
      const newCSVData = [
        [
          'AAMC ID',
          'SFMatch ID',
          'NRMP ID',
          'Last Name',
          'Middle Name',
          'First Name',
          'Medical School of Graduation',
          'Phone Number',
          'Email Address',
        ],
      ];

      validCandidates.forEach((candidate) => {
        const {
          AAMCID,
          SFMatchID,
          NRMPID,
          LastName,
          MiddleName,
          FirstName,
          MedicalSchoolOfGraduation,
          Phone,
          Email,
        } = candidate;

        const newRow = [
          AAMCID || '',
          SFMatchID || '',
          NRMPID || '',
          LastName || '',
          MiddleName || '',
          FirstName || '',
          MedicalSchoolOfGraduation || '',
          Phone || '',
          Email || '',
        ];

        newCSVData.push(newRow);
      });
      if (newCSVData.length <= 1) {
        Swal.fire('Error', 'There are no Candidates for this Department. There is nothing to print!', 'error');
        return;
      }

      setCandidateCSVData(newCSVData);
    });
  };

  const renderCandidateCSVDownload = () => {
    return (
      <div style={{ visibility: 'hidden' }}>
        <CSVLink
          data={candidateCSVData}
          target="_blank" rel="noopener noreferrer"
          filename={`${dContext.department.DepartmentName}_All_Candidates_${dayjs().format('MMM DD, YYYY hh:mm A')}`}
          ref={candidateCSVDownload}
        />
      </div>
    );
  };

  const renderLoadingModal = () => {
    return (
      <Modal isOpen={isProcessingPDF} centered={true}>
        <ModalBody>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
            <Loading
              options={{
                labelText: `Processing candidates: ${
                  pdfProgress.total > 0 ? `${pdfProgress.current} / ${pdfProgress.total}` : '. . .'
                }`,
              }}
            />
          </div>
        </ModalBody>
      </Modal>
    );
  };

  const closePdfPreviewModal = () => {
    setShowPdfPreviewModal(false);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }
    setPdfPreviewUrl(null);
    setPdfPreviewTitle('');
    setPdfPreviewFileName('');
  };

  const getTotalPreviewContextLabel = () => {
    const departmentName = dContext.department && dContext.department.DepartmentName ? dContext.department.DepartmentName : 'Department';
    return `${departmentName} Total`;
  };

  const handlePdfPreviewReady = (title) => {
    return (blobUrl, fileName, err) => {
      setIsProcessingPDF(false);
      setPdfProgress({ current: 0, total: 0 });

      if (err || !blobUrl) {
        return;
      }

      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }

      setPdfPreviewUrl(blobUrl);
      setPdfPreviewTitle(`${title} - ${getTotalPreviewContextLabel()}`);
      setPdfPreviewFileName(fileName || `${title}.pdf`);
      setShowPdfPreviewModal(true);
    };
  };

  const isDisabledButtons = isDownloadingCandidates || isProcessingPDF;

  // Mock interview date for PDF functions that need it
  const mockInterviewDate = {
    metaData: {
      DateOfInterview: null,
      StartTime: null,
      Label: 'All Candidates',
    },
    MomentDateOfInterview: null,
  };

  return (
    <div style={style.simpleRow}>
      <div style={{ ...style.simpleColumn, flex: 1 }}>
        <div style={style.simpleRow}>
          <Button
            disabled={isDisabledButtons}
            style={{ ...style.genButton, width: '100%' }}
            onClick={() => {
              setIsProcessingPDF(true);
              setPdfProgress({ current: 0, total: 0 });
              getAllCandidatesForDepartment((candidates) => {
                setPdfProgress({ current: 0, total: candidates.length });
                createPDFForCandidateBadges({
                  department: dContext.department,
                  candidates,
                  interviewDate: null,
                  onProgress: (progress) => {
                    setPdfProgress(progress);
                  },
                  previewCallback: handlePdfPreviewReady('Candidate Badges (5390)'),
                });
              });
            }}
          >
            <div style={{ ...style.spacedBetweenRow, width: '100%' }}>
              <FontAwesomeIcon icon={faIdCardAlt} style={style.buttonIcon} />
              <div style={style.buttonLabel}>Candidate Badges (5390)</div>
            </div>
          </Button>
        </div>

        <div style={style.simpleRow}>
          <Button
            disabled={isDisabledButtons}
            style={{ ...style.genButton, width: '100%' }}
            onClick={() => {
              setIsProcessingPDF(true);
              setPdfProgress({ current: 0, total: 0 });
              getAllCandidatesForDepartment((candidates) => {
                setPdfProgress({ current: 0, total: candidates.length });
                createPDFForCandidateBadges2({
                  department: dContext.department,
                  candidates,
                  interviewDate: null,
                  onProgress: (progress) => {
                    setPdfProgress(progress);
                  },
                  previewCallback: handlePdfPreviewReady('Candidate Badges (5834)'),
                });
              });
            }}
          >
            <div style={{ ...style.spacedBetweenRow, width: '100%' }}>
              <FontAwesomeIcon icon={faIdCardAlt} style={style.buttonIcon} />
              <div style={style.buttonLabel}>Candidate Badges (5834)</div>
            </div>
          </Button>
        </div>

        <div style={style.simpleRow}>
          <Button
            style={{ ...style.genButton, width: '100%' }}
            disabled={isDisabledButtons}
            onClick={() => {
              setIsProcessingPDF(true);
              setPdfProgress({ current: 0, total: 0 });
              getAllCandidatesForDepartment((candidates) => {
                setPdfProgress({ current: 0, total: candidates.length });
                createPDFForCandidateFaces({
                  department: dContext.department,
                  candidates,
                  interviewDate: mockInterviewDate,
                  onProgress: (progress) => {
                    setPdfProgress(progress);
                  },
                  previewCallback: handlePdfPreviewReady('Face Sheet Grid'),
                });
              });
            }}
          >
            <div style={{ ...style.spacedBetweenRow, width: '100%' }}>
              <FontAwesomeIcon icon={faUserAlt} style={style.buttonIcon} />
              <div style={style.buttonLabel}>Face Sheet Grid</div>
            </div>
          </Button>
        </div>
      </div>

      <div style={{ ...style.simpleColumn, flex: 1 }}>
        <div style={style.simpleRow}>
          <Button
            style={{ ...style.genButton, width: '100%' }}
            disabled={isDisabledButtons}
            onClick={() => {
              setIsProcessingPDF(true);
              setPdfProgress({ current: 0, total: 0 });
              getAllCandidatesForDepartment((candidates) => {
                setPdfProgress({ current: 0, total: candidates.length });
                createPDFForCandidateFacesWithNotes({
                  department: dContext.department,
                  candidates,
                  interviewDate: mockInterviewDate,
                  activeFields: dataTypes || [],
                  onProgress: (progress) => {
                    setPdfProgress(progress);
                  },
                  previewCallback: handlePdfPreviewReady('Face Sheet With Notes'),
                });
              });
            }}
          >
            <div style={{ ...style.spacedBetweenRow, width: '100%' }}>
              <FontAwesomeIcon icon={faUserAlt} style={style.buttonIcon} />
              <div style={style.buttonLabel}>Face Sheet With Notes</div>
            </div>
          </Button>
        </div>

        <div style={style.simpleRow}>
          <Button
            style={{ ...style.genButton, width: '100%' }}
            disabled={isDisabledButtons}
            onClick={() => {
              setIsProcessingPDF(true);
              setPdfProgress({ current: 0, total: 0 });
              getAllCandidatesForDepartment((candidates) => {
                setPdfProgress({ current: 0, total: candidates.length });
                createPDFForCandidateFactCards({
                  department: dContext.department,
                  candidates,
                  interviewDate: null,
                  onProgress: (progress) => {
                    setPdfProgress(progress);
                  },
                  previewCallback: handlePdfPreviewReady('Fact Sheet'),
                });
              });
            }}
          >
            <div style={{ ...style.spacedBetweenRow, width: '100%' }}>
              <FontAwesomeIcon icon={faAddressCard} style={style.buttonIcon} />
              <div style={style.buttonLabel}>Fact Sheet</div>
            </div>
          </Button>
        </div>

        <div style={style.simpleRow}>
          <Button
            disabled={isDisabledButtons}
            style={{ ...style.genButton, width: '100%' }}
            onClick={() => {
              downloadCandidatesCSV();
            }}
          >
            <div style={{ ...style.spacedBetweenRow, width: '100%' }}>
              <FontAwesomeIcon icon={faUsers} style={style.buttonIcon} />
              <div style={style.buttonLabel}>Candidate List (Excel CSV)</div>
            </div>
          </Button>
        </div>
      </div>
      {renderCandidateCSVDownload()}
      <PdfPreviewModal
        isOpen={showPdfPreviewModal}
        title={pdfPreviewTitle}
        pdfUrl={pdfPreviewUrl}
        fileName={pdfPreviewFileName}
        onClose={closePdfPreviewModal}
      />
      {renderLoadingModal()}
    </div>
  );
};

export default PrintDropDownTotal;
